import {Component, inject, OnInit, input, output} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewProjectDescriptionUpsertRequest,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';

@Component({
  selector: 'app-step-9-3-rfd-project-description',
  providers: [ProjectStepRichTextService],
  imports: [TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-7-3-rfd-project-description.component.html',
})
export class Step73RfdProjectDescriptionComponent
  extends ProjectBaseStepComponent
  implements OnInit
{
  private readonly invitationPreviewControllerService = inject(
    TenderInvitationPreviewControllerService,
  );
  readonly richTextService = inject(
    ProjectStepRichTextService<
      TenderInvitationPreviewProjectDescriptionUpsertRequest,
      TenderInvitationPreviewResponse
    >,
  );

  readonly preloadedData = input<TenderInvitationPreviewResponse>();
  readonly statusChange = output<TabStatus>();

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get invitationSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      TenderInvitationPreviewProjectDescriptionUpsertRequest,
      TenderInvitationPreviewResponse
    > = {
      sections: PROJECT_DESCRIPTION_SECTIONS,
      fieldMap: PROJECT_DESCRIPTION_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) =>
        this.invitationPreviewControllerService.getTenderInvitationPreview({
          projectId,
          phase: TenderPhaseType.RFD,
        }),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: TenderInvitationPreviewProjectDescriptionUpsertRequest,
  ): Observable<TenderInvitationPreviewResponse> {
    return this.invitationPreviewControllerService
      .getTenderInvitationPreview({
        projectId,
        phase: TenderPhaseType.RFD,
      })
      .pipe(
        switchMap((existingData) => {
          const completePayload: TenderInvitationPreviewProjectDescriptionUpsertRequest = {
            ...existingData,
            ...richTextPayload,
          };
          return this.invitationPreviewControllerService.upsertProjectDescription({
            projectId,
            phase: TenderPhaseType.RFD,
            tenderInvitationPreviewProjectDescriptionUpsertRequest: completePayload,
          });
        }),
      );
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();

    this.statusChange.emit({totalSections, completedSections});
  }
}

export const PROJECT_DESCRIPTION_SECTIONS: RichTextSection[] = [
  {
    key: 'step_rfd_invitation_preview_text',
    content: '',
    tooltipKey: 'project.steps.invitationTemplate.projectDescription',
    hasChanges: false,
  },
];

export const PROJECT_DESCRIPTION_FIELD_MAP: Record<
  string,
  keyof TenderInvitationPreviewProjectDescriptionUpsertRequest
> = {
  step_rfd_invitation_preview_text: 'beginningText',
};
